import { useEffect } from 'react';
import type { GetStaticPropsContext } from 'next';
import { useQuery } from '@sanity/react-loader';
import { groq } from 'next-sanity';

import { CaseStudyArticleProps as CaseStudyArticlePropsSchema } from '#/lib/sanity/fetchers/fetchCaseStudyArticleProps';
import { fetchCaseStudyPaths } from '#/lib/sanity/fetchers/fetchCaseStudyPaths';
import {
  checkPageSchema,
  type NextPageData,
} from '#/lib/sanity/fetchers/helpers';
import {
  loadQuery,
  loadQueryOptions,
  type QueryResponseInitial,
} from '#/lib/sanity/loader';
import { caseStudy, SanityCaseStudy } from '#/lib/sanity/queries/blogPost';
import { site } from '#/lib/sanity/queries/site';
import { useAnalytics } from '#/lib/segment';
import { addViewedAlgoliaObjectID } from '#/lib/viewed-algolia-objects';
import { CaseStudiesIntro } from '#/components/CaseStudies/Intro';
import { CaseStudiesQuote } from '#/components/CaseStudies/Quote';
import { CaseStudiesStats } from '#/components/CaseStudies/Stats';
import { Footnotes } from '#/components/Footnotes';
import { Headline } from '#/components/Headline';
import { Media } from '#/components/Media';
import { RelatedContent } from '#/components/RelatedContent';
import { RichText } from '#/components/RichText';
import { ArticleSchema } from '#/components/SeoSchema/ArticleSchema';
import { BreadcrumbSchema } from '#/components/SeoSchema/BreadcrumbSchema';

export const config = {
  maxDuration: 30,
};

export async function getStaticPaths() {
  const paths = await fetchCaseStudyPaths();

  return {
    paths,
    fallback: 'blocking',
  };
}

const caseStudyQuery = groq`{
  'page': ${caseStudy},
  'site': ${site},
}`;

type CaseStudyQueryResult = Omit<
  NextPageData<typeof SanityCaseStudy>,
  'draftMode'
>;

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string }>) {
  const initial = await loadQuery<CaseStudyQueryResult>(
    caseStudyQuery,
    { slug: params!.slug },
    loadQueryOptions({ draftMode }),
  );
  const data = await checkPageSchema<typeof SanityCaseStudy>({
    schema: CaseStudyArticlePropsSchema,
    result: initial.data,
    draftMode,
    context: {
      callerName: 'case study getStaticProps',
      slug: params!.slug,
    },
  });
  const props = { ...data, draftMode: !!draftMode };

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props: { ...props, initial } };
}

export default function CaseStudy(
  props: NextPageData<typeof SanityCaseStudy> & {
    initial: QueryResponseInitial<CaseStudyQueryResult>;
  },
) {
  const initialPost = props.page;
  const { data } = useQuery<CaseStudyQueryResult>(
    caseStudyQuery,
    { slug: initialPost?.href.replace('/case-studies/', '') ?? '' },
    { initial: props.initial },
  );
  const post = data.page ?? initialPost;
  const analytics = useAnalytics();

  // Track the viewed post in local storage - used for Algolia search re-ranking
  useEffect(() => {
    if (!post) return;

    addViewedAlgoliaObjectID({
      objectID: post._id,
      algoliaObjectType: 'CASE_STUDIES',
    });
    analytics.track('Viewed Case Study', {
      caseStudyTitle: post.title,
      caseStudyCategories:
        post.category?.map((category) => category.title) || [],
      caseStudyId: post._id,
    });
  }, [post, analytics]);

  if (!post) return null;

  const hideThumbnail =
    post.companyIntro ||
    post.testimonial ||
    (post?.stats && post?.stats.length > 0);
  return (
    <>
      <BreadcrumbSchema
        items={[
          { name: 'Case Studies', path: '/case-studies' },
          { name: post.title, path: post.href },
        ]}
      />
      <ArticleSchema {...post} />
      <div className="p-main mb-50 pt-40 m:mb-100 m:pt-74">
        <div className="mx-auto max-w-980">
          <Headline
            as="h1"
            value={post.title}
            variant={3}
            className="mb-10 text-pretty m:mb-30"
          />
          <p className="mb-40 mt-15 flex text-12 text-grayscale-05 m:mb-60 m:mt-30 l:mb-75 l:text-14">
            {new Date(post.publishedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
          {!hideThumbnail && (
            <div className="relative">
              <Media
                media={post.thumbnailImage}
                className="mb-40 aspect-[16/9] rounded-15 border-1 border-grayscale-03 m:mb-60 m:rounded-25 mx-auto"
                sizes={['100vw', { m: '1000px' }]}
                mediaClassName="object-cover"
                aspect={false}
                placeholder={true}
              />
            </div>
          )}
          {post.companyIntro && <CaseStudiesIntro {...post.companyIntro} />}
          {post.stats && post.stats.length > 0 && (
            <CaseStudiesStats stats={post.stats} />
          )}
          {post.testimonial && <CaseStudiesQuote {...post.testimonial} />}
          <RichText value={post.content} />
          <hr className="mb-30 mt-60 border-t-1 border-grayscale-03" />
        </div>
      </div>
      {post.relatedContent ? <RelatedContent {...post.relatedContent} /> : null}
      {post.footnotes ? <Footnotes {...post.footnotes} /> : null}
    </>
  );
}
